import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import { FaPrint } from 'react-icons/fa';

import PatientDashboardPdf from '../../components/PDFComponent/PDFComponent'; 
import NavbarComponent from '../../components/Navbar/NavbarComponent';
import NavMenuComponent from '../../components/NavMenu/NavMenuComponent';
import DashBoardComponent from '../../components/DashBoard/DashBoardComponent';
import ScoreDashboard from '../../components/GraficoRespostas/GraficoRespostas';

import styles from './index.module.css';
import api from '../../service/api';

function PatientDetails() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const nomePaciente = location.state?.nome || 'Nome não disponível';
  const sexoPaciente = location.state?.sexo || null;
  const cpfPaciente = location.state?.cpf || null;

  const [vida, setVida] = useState({ nome: nomePaciente, tendencias: [] });
  const [resultados, setResultados] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const processarDadosvida = useCallback(
    (dadosApi) => {
      const questionarioMap = {
        2: 'Ansiedade',
        3: 'Depressão',
        4: 'Bipolaridade',
        5: 'Dependência',
        6: 'Alimentação',
        7: 'IRIS',
        8: 'SONO',
        9: 'Burnout',
      };

      const categorias = [
        'Alimentação',
        'Ansiedade',
        'Bipolaridade',
        'Burnout',
        'Dependência',
        'Depressão',
        'IRIS',
        'SONO',
      ];
      const tendencias = categorias.map(() => 0);

      dadosApi.resultados.forEach((resultado) => {
        resultado.scores.forEach((scoreObj) => {
          const questionarioId = parseInt(scoreObj.questionario_id, 10);
          const categoria = questionarioMap[questionarioId];
          const valorNormalizado = (parseFloat(scoreObj.score) / 100) * 5;

          const indexCategoria = categorias.indexOf(categoria);
          if (indexCategoria !== -1) {
            tendencias[indexCategoria] = valorNormalizado;
          }
        });
      });

      return { nome: nomePaciente, tendencias };
    },
    [nomePaciente]
  );

  useEffect(() => {
    const fetchVida = async () => {
      try {
        const { data } = await api.get(`/score/${id}`);

        const vidaProcessado = processarDadosvida(data);
        setVida(vidaProcessado);

        const resultadosOrdenados = [...(data.resultados || [])].reverse();
        setResultados(resultadosOrdenados);
      } catch (error) {
        console.error('Erro ao buscar dados do paciente:', error);
        setVida(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVida();
  }, [id, processarDadosvida]);

  const handleGeneratePdf = async () => {
    if (!resultados || resultados.length === 0) {
      console.error('Não há resultados disponíveis para gerar PDF.');
      return;
    }

    // Pega o envioId do item mais recente (primeiro após o reverse)
    const envioId = resultados[0].envio_id;

    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      console.error('Popup bloqueado pelo navegador.');
      return;
    }

    newWindow.document.title = 'Gerando PDF...';
    newWindow.document.body.style.margin = '0';
    newWindow.document.body.style.display = 'flex';
    newWindow.document.body.style.alignItems = 'center';
    newWindow.document.body.style.justifyContent = 'center';
    newWindow.document.body.innerHTML =
      '<p style="font-family: Helvetica, Arial, sans-serif; font-size: 36px;">Gerando PDF, por favor aguarde...</p>';

    try {
      const { data: report } = await api.get(
        `/report/${encodeURIComponent(envioId)}`
      );

      const doc = pdf(
        <PatientDashboardPdf
          report={report}
          patientName={nomePaciente}
          patientSexo={sexoPaciente}
          patientCPF={cpfPaciente}
        />
      );
      const blob = await doc.toBlob();
      const url = URL.createObjectURL(blob);

      newWindow.location.href = url;
    } catch (err) {
      console.error('Falha ao gerar PDF:', err);
      newWindow.close();
    }
  };

  if (isLoading) {
    return (
      <div className={styles.mainDiv}>
        <NavbarComponent />
        <NavMenuComponent />
        <div className={styles.content}>
          <p>Carregando dados do paciente...</p>
        </div>
      </div>
    );
  }

  if (!vida) {
    return (
      <div className={styles.mainDiv}>
        <NavbarComponent />
        <NavMenuComponent />
        <div className={styles.content}>
          <p>Paciente não encontrado.</p>
          <button
            onClick={() => navigate(-1)}
            className={styles.backButton}
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mainDiv}>
      <NavbarComponent />
      <NavMenuComponent />
      <div className={styles.content}>
        <div className={styles.headerRow}>
          <h1 className={styles.pageTitle}>Tendências de {vida.nome}</h1>
          <div className={styles.buttonGroup}>
            <button onClick={handleGeneratePdf} className={styles.backButton}>
              Exportar Relatório
              <FaPrint style={{ marginLeft: '8px' }} />
            </button>
            <button
              onClick={() => navigate(-1)}
              className={styles.backButton}
            >
              Voltar
            </button>
          </div>
        </div>

        <DashBoardComponent vidas={[vida]} />

        {resultados.map((res, index) => (
          <div key={index} className={styles.marginTop}>
            <ScoreDashboard scores={res.scores} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default PatientDetails;
